import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ButtonBase, ListItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { UserWithRoles } from '~/modules/common/components/User';
import IconLinkButton from '~/modules/resourcing/common/components/IconLinkButton';
import {
  useBulkAcceptAllocationsForResourceRequest,
  useBulkProposeAllocationsForResourceRequest
} from '~/modules/resourcing/common/hooks';
import {
  hasEditProjectAndTeamPermission,
  hasResourceAllocationEditPermission
} from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/ResourceRequestChartContent/components/util';
import RESOURCE_MATCH_POPOVER_BUTTON_ACTION from '../enum';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.25, 0)
  },
  resourceColumn: {
    display: 'flex',
    flex: 1,
    minWidth: theme.spacing(20)
  },
  smartMatchColumn: {
    minWidth: theme.spacing(12),
    textAlign: 'right'
  },
  availabilityColumn: {
    minWidth: theme.spacing(12),
    textAlign: 'right'
  },
  availabilityLow: {
    color: theme.palette.error.main
  },
  button: {
    color: theme.palette.common.white,
    textTransform: 'uppercase',
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.75, 1.25),
    boxShadow: theme.shadows[2],
    marginLeft: theme.spacing(2)
  }
}));

const useUserWithRolesStyles2 = makeStyles(theme => ({
  root: {
    paddingRight: theme.spacing(1)
  }
}));

const useIconLinkButtonStyles = makeStyles(theme => ({
  openButton: {
    color: theme.palette.text.primary,
    opacity: '0.8'
  }
}));

export const ResourceMatchListRow = ({
  me,
  resourceRequest,
  resourceMatch,
  user,
  onClose,
  allocationChartRef,
  buttonType,
  project
}) => {
  const classes = useStyles();
  const userWithRolesClasses2 = useUserWithRolesStyles2();
  const iconLinkButtonClasses = useIconLinkButtonStyles();
  const { formatMessage } = useIntl();

  const {
    bulkAcceptAllocationsForResourceRequest
  } = useBulkAcceptAllocationsForResourceRequest();

  const {
    bulkProposeAllocationsForResourceRequest
  } = useBulkProposeAllocationsForResourceRequest();

  const userDrawerLabel = formatMessage(
    {
      id: 'resourceMatchPopover.drawer'
    },
    { user: user.displayText }
  );
  const hasPermission =
    hasEditProjectAndTeamPermission(resourceRequest, project) &&
    hasResourceAllocationEditPermission(me.permissions);

  const onAcceptAction = useCallback(() => {
    bulkAcceptAllocationsForResourceRequest({
      acceptAllocationInput: [
        {
          resourceRequestId: resourceRequest.id,
          userIds: [user.uri]
        }
      ],
      allocationChartRef
    });
    onClose();
  }, [
    bulkAcceptAllocationsForResourceRequest,
    resourceRequest,
    user,
    allocationChartRef,
    onClose
  ]);

  const onProposeAction = useCallback(() => {
    bulkProposeAllocationsForResourceRequest({
      submittedResourceRequestsWithMatches: [
        {
          resourceRequest,
          userIds: resourceMatch ? [resourceMatch.userUri] : []
        }
      ]
    });
    onClose();
  }, [
    resourceRequest,
    resourceMatch,
    bulkProposeAllocationsForResourceRequest,
    onClose
  ]);

  const isLowAvailability =
    resourceMatch.availabilityPercentageInDateRange < resourceRequest.load;

  return (
    <ListItem
      data-qe-id={`${resourceMatch.id}_ResourceMatchListRow`}
      className={classes.root}
    >
      <div className={classes.resourceColumn}>
        <UserWithRoles classes={userWithRolesClasses2} user={user} />
        <IconLinkButton
          classes={iconLinkButtonClasses}
          ariaLabel={userDrawerLabel}
          toLink={`#/resource/${user.slug}`}
        />
      </div>
      <div className={classes.smartMatchColumn}>
        <Typography variant="subtitle1">
          {formatMessage(
            {
              id: 'resourceMatchPopover.score'
            },
            { score: resourceMatch.score }
          )}
        </Typography>
      </div>
      <div className={classes.availabilityColumn}>
        <Typography
          variant="subtitle1"
          className={isLowAvailability ? classes.availabilityLow : undefined}
        >
          {formatMessage(
            {
              id: 'resourceMatchPopover.availabilityVal'
            },
            { availability: resourceMatch.availabilityPercentageInDateRange }
          )}
        </Typography>
      </div>
      {hasPermission && (
        <ButtonBase
          className={classes.button}
          onClick={
            buttonType === RESOURCE_MATCH_POPOVER_BUTTON_ACTION.PROPOSE
              ? onProposeAction
              : onAcceptAction
          }
        >
          <Typography variant="subtitle1">
            {formatMessage({
              id:
                buttonType === RESOURCE_MATCH_POPOVER_BUTTON_ACTION.PROPOSE
                  ? 'resourceMatchPopover.propose'
                  : 'resourceMatchPopover.accept'
            })}
          </Typography>
        </ButtonBase>
      )}
    </ListItem>
  );
};

ResourceMatchListRow.propTypes = {
  me: PropTypes.object.isRequired,
  resourceRequest: PropTypes.object.isRequired,
  resourceMatch: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  allocationChartRef: PropTypes.object,
  buttonType: PropTypes.oneOf([
    RESOURCE_MATCH_POPOVER_BUTTON_ACTION.ACCEPT,
    RESOURCE_MATCH_POPOVER_BUTTON_ACTION.PROPOSE
  ]).isRequired,
  project: PropTypes.object.isRequired
};

export default ResourceMatchListRow;
